import {
  getGridStringOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridBooleanOperators,
  GridFilterOperator,
} from '@mui/x-data-grid-pro';
import { MetadataFieldType } from '@/api/generatedApi';
import type { LabelOption } from '@/api/api';
import { MetadataFilterInput, type MetadataFilterOption } from './MetadataFilterInput';
import { LabelFilterInput } from '@/components/ContentsTable/LabelFilterInput';
import { GRID_NUMERIC_OPERATORS } from './numericOperators';

export type { MetadataFilterOption } from './MetadataFilterInput';

const createFilterOperatorsWithInput = (
  options: MetadataFilterOption[],
  onApply?: () => void
): GridFilterOperator[] => {
  return getGridStringOperators()
    .filter(op => op.value === 'isAnyOf')
    .map(
      operator =>
        ({
          ...operator,
          InputComponent: MetadataFilterInput,
          InputComponentProps: { options, onApply },
        }) as GridFilterOperator
    );
};

export const getExtensionFilterOperators = (
  options?: MetadataFilterOption[],
  onApply?: () => void
): GridFilterOperator[] => createFilterOperatorsWithInput(options ?? [], onApply);

export const getFolderFilterOperators = (
  options?: MetadataFilterOption[],
  onApply?: () => void
): GridFilterOperator[] => createFilterOperatorsWithInput(options ?? [], onApply);

export const getDateFilterOperators = (): GridFilterOperator[] =>
  getGridDateOperators().filter(op =>
    ['is', 'after', 'before', 'onOrAfter', 'onOrBefore', 'isEmpty'].includes(op.value)
  );

export const getCreatorFilterOperators = (): GridFilterOperator[] =>
  getGridStringOperators().filter(op => ['contains', 'equals', 'isEmpty'].includes(op.value));

export const getStringMetadataFilterOperators = (): GridFilterOperator[] =>
  getGridStringOperators().filter(op =>
    ['equals', 'contains', 'startsWith', 'endsWith', 'isAnyOf', 'isEmpty'].includes(op.value)
  );

const isEmptyOperator: GridFilterOperator = {
  value: 'isEmpty',
  // eslint-disable-next-line unicorn/no-null
  getApplyFilterFn: () => null,
  requiresFilterValue: false,
};

export const getNumericMetadataFilterOperators = (): GridFilterOperator[] => [
  ...getGridNumericOperators().filter(op =>
    GRID_NUMERIC_OPERATORS.includes(op.value as (typeof GRID_NUMERIC_OPERATORS)[number])
  ),
  isEmptyOperator,
];

export const getBooleanMetadataFilterOperators = (): GridFilterOperator[] => [
  ...getGridBooleanOperators(),
  isEmptyOperator,
];

export const getStringMetadataFilterOperatorsWithOptions = (
  options: MetadataFilterOption[],
  onApply?: () => void
): GridFilterOperator[] => createFilterOperatorsWithInput(options, onApply);

export const getMetadataFilterOperators = (
  fieldType: MetadataFieldType | undefined,
  options?: MetadataFilterOption[],
  onApply?: () => void
): GridFilterOperator[] => {
  if (fieldType === MetadataFieldType.Number) return getNumericMetadataFilterOperators();
  if (fieldType === MetadataFieldType.Boolean) return getBooleanMetadataFilterOperators();
  if (options && options.length > 0) {
    return getStringMetadataFilterOperatorsWithOptions(options, onApply);
  }
  return getStringMetadataFilterOperators();
};

export const getLabelFilterOperators = (labelOptions?: LabelOption[]): GridFilterOperator[] => {
  return getGridStringOperators()
    .filter(op => op.value === 'isAnyOf')
    .map(
      operator =>
        ({
          ...operator,
          InputComponent: LabelFilterInput,
          InputComponentProps: { labelOptions },
        }) as GridFilterOperator
    );
};
